import {RespinSymbolData, Logic} from '@fantasmagames/slot/src/logic/Logic';
import {
	AvalancheNewSymbol,
	ExpandingWildReel, ExplosionInfo,
	GameWinLine,
	RespinScatterInfo,
	SpinResult,
} from '@fantasmagames/slot/src/logic/SpinResult';
import {GameSymbol} from '@fantasmagames/slot/src/modules/reels/data/GameSymbol';
import {ReelConstants, SymbolPosition} from '@fantasmagames/slot/src/modules/reels/ReelConstants';

export interface PrototypeWinLine {
	winType: string;
	lineIndex: number;
	win: number;
	winPositions: SymbolPosition[];
	winSymbol: number;
	winLength: number;
}

export interface AvalancheResult {
	initialSymbols: GameSymbol[][];
	symbolsAfterRemoval: GameSymbol[][];
	newSymbols: GameSymbol[][];
	winnings: PrototypeWinLine[];
	win: number;
}

export class PrototypeSpinResult extends SpinResult {
	private _scattersNum: number = 0;

	public cleanup(): void {
		this._winLines = [];
		this._scattersNum = 0;
	}

	public setReelsForWinning(gamereels: GameSymbol[]): void {
		this._reels = [];
		for (let reelIndex: number = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			this.reels.push([]);
			for (let rowIndex: number = 0; rowIndex < ReelConstants.ROW_AMOUNT; rowIndex++) {
				this._reels[reelIndex].push(gamereels[reelIndex * ReelConstants.ROW_AMOUNT + rowIndex]);
			}
		}
	}

	public setSymbolsToRemove(avalanche: AvalancheResult, avalancheReels: number[]): void {
		this._symbolsToRemove = [];
		for (const winnning of avalanche.winnings) {
			for (const pos of winnning.winPositions) {
				if (!this.hasExpandingWildOnReel(pos.x, avalancheReels)) {
					const hasSymbol: boolean = !!this._symbolsToRemove.find( (item) => {
						return item.x === pos.x && item.y === pos.y;
					});
					if (!hasSymbol) {
						this._symbolsToRemove.push(pos);
					}
				}
			}
		}

		const ewReels: number[] = [];
		if (avalancheReels.length > 0) {
			for (const wildIndex of avalancheReels) {
				if (this.hasWinningOnReel(wildIndex, avalanche)) {
					ewReels.push(wildIndex);
				}
			}
		}

		this.setExpandedWildsToRemove(ewReels);
	}

	public setSymbolsToAdd(avalanche: AvalancheResult): void {
		this._symbolsToAdd = [];
		for (let reel = 0; reel < avalanche.newSymbols.length; reel++) {
			for (let row = 0; row < avalanche.newSymbols[reel].length; row++) {
				this._symbolsToAdd.push(new AvalancheNewSymbol(reel, row, avalanche.newSymbols[reel][row].sym, 1));
			}
		}

	}

	public clearSymbolsToAdd(): void {
		this._symbolsToAdd = [];
	}

	protected hasExpandingWildOnReel(reelIndex: number, avalancheReels: number[]): boolean {
		for (const avalancheReel of avalancheReels) {
			if (reelIndex === avalancheReel) {
				return true;
			}
		}

		return false;
	}

	protected hasPositionInWinLines(position: SymbolPosition, avalanche: AvalancheResult): boolean {
		for (const winnning of avalanche.winnings) {
			const index: number = winnning.winPositions.findIndex( (elem) => {
				return elem.x === position.x && elem.y === position.y;
			} );
			if (index >= 0) {
				return true;
			}
		}

		return false;
	}

	protected hasWinningOnReel(reelIndex: number, avalanche: AvalancheResult): boolean {
		for (const winnning of avalanche.winnings) {
			const index: number = winnning.winPositions.findIndex( (elem) => {
				return elem.x === reelIndex;
			} );
			if (index >= 0) {
				return true;
			}
		}

		return false;
	}

	protected setExpandedWildsToRemove(expandedWildsToRemove: number[]): void {
		this._expandedWildsToRemove = expandedWildsToRemove;
	}

	public setWins(wins: PrototypeWinLine[]): void {
		this._winLines = [];
		if (!wins) {
			return;
		}

		for (const win of wins) {
			this._winLines.push(
				new GameWinLine(-1, win.winSymbol, win.winPositions, win.winPositions, win.win, 0, win.winPositions.length),
			);
		}
	}

	public setTotalWin(win: number): void {
		this._win = win;
	}

	public setRoundWin(win: number): void {
		this._spinWin = win;
	}

	public setCurrentRoundSymbols(symbols: GameSymbol[][]): void {
		if (symbols) {
			this._reels = [];
			for (let reelIndex = 0; reelIndex < symbols.length; reelIndex++) {
				this._reels[reelIndex] = [];
				for (let rowIndex = 0; rowIndex < symbols[reelIndex].length; rowIndex++) {
					const sym: GameSymbol = symbols[reelIndex][rowIndex];
					this._reels[reelIndex].push(
						new GameSymbol(
							sym.sym,
							{width: 1, height: 1},
							{
								x: sym.position.x,
								y: sym.position.y,
							},
						),
					);
				}
			}
		}
	}

	public setRespins(respin: RespinSymbolData[], locked: RespinSymbolData[], newSymbols: GameSymbol[]): void {
		this._individualSymbolsRespins = respin;
		this._individualSymbolsLocked = locked;
		this._individualSymbolsNewSymbols = newSymbols;
	}

	public setBalance(value: number): void {
		this._balance = value;
	}

	public get balance(): number {
		return this._balance * Logic.CENT_TO_A_DOLLAR;
	}

	public get win(): number {
		return this._win * Logic.CENT_TO_A_DOLLAR;
	}

	public get spinWin(): number {
		return this._spinWin * Logic.CENT_TO_A_DOLLAR;
	}

	public setStopPositions(positions: number[]): void {
		this._stopPositions = positions;
	}

	public get individualSymbolsRespins(): RespinSymbolData[] {
		return this._individualSymbolsRespins;
	}

	public get individualSymbolsNewSymbols(): GameSymbol[] {
		return this._individualSymbolsNewSymbols;
	}

	public get individualSymbolsLocked(): RespinSymbolData[] {
		return this._individualSymbolsLocked;
	}

	public get scattersNum(): number {
		return this._scattersNum;
	}

	public set scattersNum(value: number) {
		this._scattersNum = value;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/logic/prototype/PrototypeSpinResult.ts